import type { JSX } from 'react';
import { NavLink, generatePath, useLocation } from 'react-router';

import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { ACTION_LISTING, AI_DOC_ANALYZER_LISTING, ANALYTICS_PATH, COMPLIANCE_REQUIREMENT_LISTING, GOVERNANCE_LISTING, RISK_LISTING, RISK_REVIEW_LISTING, TPRM_FOLDERS_LISTING, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_FOLDERS_LISTING, UCRM_ISSUES_EXCEPTIONS } from 'Config/PathTemplates';
import { ALERTS, DASHBOARDS, EXCEPTIONS, EXPORTS, FRAMEWORKS, HISTORY, ISSUES, ISSUES_EXCEPTIONS, OPERATIONAL_CONTROLS, TPRM, UCRM } from 'Config/Paths';
import { ANALYTICS_ROLES, CONTROL_AUTOMATION_ROLES, REPORTING_AND_EXPORTS_ROLES, RISK_MANAGER_ROLES, RISK_ROLES, TPRM_ROLES, UCRM_ROLES } from 'Helpers/UserUtils';
import { AdvancedFeature } from 'Models/ClientDetailsModel';

import styles from './Nav.module.css';
import { NavHeader } from './NavHeader/NavHeader';
import { NavLogo } from './NavLogo/NavLogo';
import { NavSubHeader } from './NavSubHeader/NavSubHeader';

interface NavProps {
    setNavOpen: (open: boolean) => void;
}

export const Nav = (props: NavProps): JSX.Element => {
    const cachedData = useCachedData();
    const location = useLocation();

    const navLinkProps = (pathForNavItem: string) => {
        if (pathForNavItem === `/`) {
            if (location.pathname === `/`) {
                return { to: '/', className: styles.SideMenuItemActive };
            } else {
                return { to: pathForNavItem, className: styles.SideMenuItem };
            }
        }

        // Exceptional cases where our real paths conflict with the paths "assigned" to menu items. Some refactoring may obviate the need for this section.
        if (location.pathname.startsWith(`/${ISSUES}`) || location.pathname.startsWith(`/${EXCEPTIONS}`)) {
            return { to: pathForNavItem, className: pathForNavItem === `/${ISSUES_EXCEPTIONS}` ? styles.SideMenuItemActive : styles.SideMenuItem };
        }
        if (location.pathname.startsWith(`/${TPRM}/${ISSUES}`) || location.pathname.startsWith(`/${TPRM}/${EXCEPTIONS}`)) {
            return { to: pathForNavItem, className: pathForNavItem === `/${TPRM}/${ISSUES_EXCEPTIONS}` ? styles.SideMenuItemActive : styles.SideMenuItem };
        }
        if (location.pathname.startsWith(`/${TPRM}/${HISTORY}`)) {
            return { to: pathForNavItem, className: pathForNavItem === TPRM_THIRD_PARTIES_AND_SERVICES_LISTING ? styles.SideMenuItemActive : styles.SideMenuItem };
        }
        if (location.pathname.startsWith(`/${UCRM}/${ISSUES}`) || location.pathname.startsWith(`/${UCRM}/${EXCEPTIONS}`)) {
            return { to: pathForNavItem, className: pathForNavItem === `/${UCRM}/${ISSUES_EXCEPTIONS}` ? styles.SideMenuItemActive : styles.SideMenuItem };
        }
        if (location.pathname.startsWith(`/${UCRM}/${HISTORY}`)) {
            return { to: pathForNavItem, className: pathForNavItem === UCRM_BUSINESSES_AND_USE_CASES_LISTING ? styles.SideMenuItemActive : styles.SideMenuItem };
        }

        // Default: highlight only the menu item whose path corresponds with the current location.
        if (location.pathname.startsWith(pathForNavItem)) {
            return { to: pathForNavItem, className: styles.SideMenuItemActive };
        } else {
            return { to: pathForNavItem, className: styles.SideMenuItem };
        }
    };

    const closeMenuUrl = new URL('/close-menu.svg', import.meta.url).href;
    return (
        <div className={styles.Nav}>
            <div className={styles.navContent}>
                <div className={styles.closeMenu}>
                    <div className={styles.closeMenuButton}>
                        <IconButton aria-label="Close Menu Navigation" onClick={() => props.setNavOpen(false)} iconImage={<img src={closeMenuUrl} alt="close-menu-navigation" />} />
                    </div>
                </div>
                <NavHeader />
                <div className={styles.scrollContainer}>
                    <nav className={styles.Section}>
                        <NavSubHeader>My Assignments</NavSubHeader>
                        <NavLink {...navLinkProps('/')}>My Assignments</NavLink>
                    </nav>
                    <RBACComponent roles={REPORTING_AND_EXPORTS_ROLES}>
                        <nav className={styles.Section}>
                            <NavSubHeader>Reporting and Exports</NavSubHeader>
                            <RBACComponent roles={ANALYTICS_ROLES}>{cachedData.clientHasAdvancedFeature([AdvancedFeature.ANALYTICS]) && <NavLink {...navLinkProps(generatePath(ANALYTICS_PATH))}>Analytics</NavLink>}</RBACComponent>
                            <NavLink {...navLinkProps(`/${DASHBOARDS}`)}>Dashboards</NavLink>
                            <NavLink {...navLinkProps(`/${EXPORTS}`)}>Exports</NavLink>
                        </nav>
                    </RBACComponent>
                    <RBACComponent roles={RISK_ROLES}>
                        <nav className={styles.Section}>
                            <NavSubHeader>Risk Management</NavSubHeader>
                            <NavLink {...navLinkProps(generatePath(RISK_LISTING))}>Dynamic Risk Register</NavLink>
                            <RBACComponent roles={RISK_MANAGER_ROLES}>
                                <NavLink {...navLinkProps(generatePath(RISK_REVIEW_LISTING))}>Risk Review</NavLink>
                            </RBACComponent>
                            <NavLink {...navLinkProps(ACTION_LISTING)}>Action Planning</NavLink>
                        </nav>
                    </RBACComponent>
                    <RBACComponent roles={CONTROL_AUTOMATION_ROLES}>
                        <nav className={styles.Section}>
                            <NavSubHeader>Control Automation</NavSubHeader>
                            <NavLink {...navLinkProps(`/${OPERATIONAL_CONTROLS}/${FRAMEWORKS}`)}>Operational Controls</NavLink>
                            <NavLink {...navLinkProps(generatePath(GOVERNANCE_LISTING))}>Governance Integration</NavLink>
                            <NavLink {...navLinkProps(generatePath(COMPLIANCE_REQUIREMENT_LISTING))}>Compliance Requirements</NavLink>
                            <NavLink {...navLinkProps(`/${ISSUES_EXCEPTIONS}`)}>Issues and Exceptions</NavLink>
                            <NavLink {...navLinkProps(`/${ALERTS}`)}>Alerts and Limits</NavLink>
                        </nav>
                    </RBACComponent>
                    <nav className={styles.Section}>
                        <NavSubHeader>Third-Party Risk Management</NavSubHeader>
                        <NavLink {...navLinkProps(generatePath(TPRM_THIRD_PARTIES_AND_SERVICES_LISTING))}>Third Parties and Services</NavLink>
                        <RBACComponent roles={TPRM_ROLES}>
                            <NavLink {...navLinkProps(`/${TPRM}/${ISSUES_EXCEPTIONS}`)}>Issues and Exceptions</NavLink>
                        </RBACComponent>
                        <RBACComponent roles={TPRM_ROLES}>
                            <NavLink {...navLinkProps(TPRM_FOLDERS_LISTING)}>Folders</NavLink>
                        </RBACComponent>
                    </nav>
                    {cachedData.clientHasAdvancedFeature([AdvancedFeature.AI_DOCUMENT_ANALYZER_QUESTIONNAIRE, AdvancedFeature.AI_DOCUMENT_ANALYZER_SOC]) && (
                        <nav className={styles.Section}>
                            <NavSubHeader>Document Analyzer</NavSubHeader>
                            <NavLink {...navLinkProps(generatePath(AI_DOC_ANALYZER_LISTING))}>Document Analyzer</NavLink>
                        </nav>
                    )}
                    {cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]) && (
                        <nav className={styles.Section}>
                            <NavSubHeader>AI Governance</NavSubHeader>
                            <NavLink {...navLinkProps(generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING))}>Businesses and Use Cases</NavLink>
                            <RBACComponent roles={UCRM_ROLES}>
                                <NavLink {...navLinkProps(generatePath(UCRM_ISSUES_EXCEPTIONS))}>Issues and Exceptions</NavLink>
                            </RBACComponent>
                            <RBACComponent roles={UCRM_ROLES}>
                                <NavLink {...navLinkProps(generatePath(UCRM_FOLDERS_LISTING))}>Folders</NavLink>
                            </RBACComponent>
                        </nav>
                    )}
                </div>
            </div>
            <div>
                <NavLogo />
            </div>
        </div>
    );
};
